import { useMutation } from "@tanstack/react-query";

import { trpcReact } from "~/trpc/client";
import { type RouterOutputs } from "~/trpc/types";

export type Cart = RouterOutputs["cart"]["cart"];

export type TourCartLine = Extract<
  NonNullable<Cart>["cartLines"][0],
  { type: "bokun" | "linktivity" }
>;

export type TripCartLine = Extract<
  NonNullable<Cart>["cartLines"][0],
  { type: "trip" }
>;

export function useCart(onChange?: () => Promise<void>) {
  const utils = trpcReact.useUtils();

  const changeMutation = useMutation({
    mutationFn: async () => {
      await onChange?.();
    },
    mutationKey: ["useCart", "onChange"],
  });

  function onSuccess(cart: Omit<Cart, "order">) {
    if (onChange) {
      void changeMutation.mutateAsync();
    }
    utils.cart.cart.setData(undefined, (data) =>
      data ? { ...data, ...cart } : undefined,
    );
  }

  const cart = trpcReact.cart.cart.useQuery(undefined, {
    refetchOnWindowFocus: true,
  });

  const update = trpcReact.cart.update.useMutation();

  const removeTourCartLine = trpcReact.cart.removeTourCartLine.useMutation({
    onSuccess,
  });

  const addTourCartLine = trpcReact.cart.addTourCartLine.useMutation({
    onSuccess,
  });

  const removeTripCartLine = trpcReact.cart.removeTripCartLine.useMutation({
    onSuccess,
  });

  // Deprecated trip mutations removed - use trip action mutations directly in components

  return {
    isLoading:
      cart.isPending ||
      removeTourCartLine.isPending ||
      changeMutation.isPending,
    cart: cart.data ?? null,
    update,
    addTourCartLine,
    removeTourCartLine,
    removeTripCartLine,
    isEmpty:
      cart.isFetched &&
      cart.data &&
      (cart.data.cartLines.length === 0 || cart.data.dueNowAmount <= 0),
  };
}
